import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link } from "react-router-dom";
import type { ReorderSuggestion } from "@dentacore/shared-types";

import { STOCK_MOVED } from "../api/stock-moved";
import { listReorder } from "../api/suppliers";
import { useActiveBranch } from "../api/use-active-branch";
import { useAuth } from "../auth/auth-context";
import { useCan } from "../auth/use-can";
import { useModules } from "../shell/modules-context";
import { useSeesPerUseCost } from "./use-sees-per-use-cost";
import { recommendationText, ReorderActions } from "./reorder-actions";
import inv from "./inventory.module.css";
import styles from "./suppliers.module.css";

/**
 * The low-stock popup — STORY-171, SU-D3 and SU-D16.
 *
 * ── How an open screen finds out ────────────────────────────────────────────
 *
 * The server already re-checks an item every time its count moves (STORY-112)
 * and raises a notice when it crosses a quarter of a full shelf. What it cannot
 * do is tell a screen that is already open, because DentaCore has no live push.
 * So this asks — **at once after this screen moved a count** (`checkNow`, called
 * after an adjustment, an opened unit or a completed visit), **on returning to
 * the tab, and once a minute otherwise**. The person who caused it sees it
 * immediately; everyone else at the location within a minute (SU-D16).
 *
 * ── Once per person ─────────────────────────────────────────────────────────
 *
 * Each notice pops up once for each person on this computer, then lives on
 * Home until it is ordered or restocked. "Seen" is remembered in this browser
 * only — a per-person convenience, never a record. A notice that clears and is
 * raised again is a new notice, so it pops up again, which is right: the item
 * went low again. An item already on order never pops up.
 */

const POLL_MS = 60_000;

interface LowStockWatch {
  /** Bumped whenever something low-stock may have changed. */
  signal: number;
  /** Ask the server now — after this screen moved a count. */
  checkNow: () => void;
}

const Context = createContext<LowStockWatch>({ signal: 0, checkNow: () => undefined });

/** Re-read whatever depends on low stock when this changes (Home's notices). */
export function useLowStockSignal(): number {
  return useContext(Context).signal;
}

/** Call after moving a count, so this person's popup is immediate. */
export function useLowStockCheck(): () => void {
  return useContext(Context).checkNow;
}

const seenKey = (userId: string): string => `dentacore.lowStockSeen.${userId}`;

function readSeen(userId: string): Set<string> {
  try {
    const raw = window.localStorage.getItem(seenKey(userId));
    return new Set(raw === null ? [] : (JSON.parse(raw) as string[]));
  } catch {
    return new Set();
  }
}

function writeSeen(userId: string, seen: Set<string>): void {
  try {
    // The newest few hundred are plenty; the list must not grow for ever.
    window.localStorage.setItem(seenKey(userId), JSON.stringify([...seen].slice(-300)));
  } catch {
    /* Private window or storage refused: the popup may show again. Harmless. */
  }
}

export function LowStockWatchProvider({
  children,
}: {
  children: React.ReactNode;
}): React.JSX.Element {
  const branchId = useActiveBranch();
  const { user } = useAuth();
  const can = useCan();
  // The popup offers who to order from, which is Supply's (STORY-188d, MOD-D3).
  // Only once the modules are known: until then `has` says yes, and asking would be refused.
  const modules = useModules();
  // Price per use needs Accounting and accounting.view (QF-D20).
  const seesPerUseCost = useSeesPerUseCost();
  const hasSupply = modules.states !== null && modules.writable("SUPPLY");
  const watching = user !== null && branchId !== null && can("inventory.view") && hasSupply;
  const canOrder = can("inventory.movement.record");
  // Supplier prices, so the home currency (QA4-D11) — not the location's.
  const currency = user?.currency ?? "USD";

  const [signal, setSignal] = useState(0);
  const [showing, setShowing] = useState<ReorderSuggestion | null>(null);
  const [error, setError] = useState<string | null>(null);
  const inFlight = useRef(false);

  const check = useCallback(async () => {
    if (!watching || user === null || inFlight.current) return;
    inFlight.current = true;
    try {
      const list = await listReorder();
      const seen = readSeen(user.id);
      const next = list.find((s) => !seen.has(s.alertId) && s.openOrder === null) ?? null;
      setShowing((current) => current ?? next);
    } catch {
      /* A popup is a courtesy; Home still shows the notice. */
    } finally {
      inFlight.current = false;
    }
  }, [watching, user]);

  const checkNow = useCallback(() => {
    setSignal((n) => n + 1);
    void check();
  }, [check]);

  useEffect(() => {
    if (!watching) return;
    void check();
    const timer = window.setInterval(() => void check(), POLL_MS);
    const onVisible = (): void => {
      if (document.visibilityState === "visible") void check();
    };
    document.addEventListener("visibilitychange", onVisible);
    // This screen just moved a count: ask now rather than within the minute.
    const onMoved = (): void => checkNow();
    window.addEventListener(STOCK_MOVED, onMoved);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener(STOCK_MOVED, onMoved);
    };
  }, [watching, check, checkNow, branchId]);

  // A different location's notice is not this screen's business.
  useEffect(() => setShowing(null), [branchId]);

  const dismiss = useCallback(() => {
    if (showing !== null && user !== null) {
      const seen = readSeen(user.id);
      seen.add(showing.alertId);
      writeSeen(user.id, seen);
    }
    setShowing(null);
    setError(null);
  }, [showing, user]);

  const value = useMemo(() => ({ signal, checkNow }), [signal, checkNow]);

  return (
    <Context.Provider value={value}>
      {children}
      {showing !== null && (
        <div
          className={styles.popup}
          role="status"
          aria-live="polite"
          data-testid="low-stock-popup"
        >
          <p className={styles.popupTitle}>
            {showing.itemName} is running low — {showing.quantityOnHand} uses left
          </p>
          <p className={styles.note}>{recommendationText(showing, currency, seesPerUseCost)}</p>
          {error !== null && <p className={styles.error}>{error}</p>}
          <div className={styles.popupActions}>
            <button
              type="button"
              className={inv.quiet}
              onClick={dismiss}
              data-testid="low-stock-later"
            >
              Later
            </button>
            <Link
              className={inv.ghost}
              to={`/inventory?item=${encodeURIComponent(showing.itemId)}`}
              onClick={dismiss}
            >
              Open the item
            </Link>
            <ReorderActions
              suggestion={showing}
              currency={currency}
              canOrder={canOrder}
              compact
              onError={setError}
              onChanged={() => {
                dismiss();
                setSignal((n) => n + 1);
              }}
            />
          </div>
        </div>
      )}
    </Context.Provider>
  );
}
